"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="207588" className="flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative font-default uppercase tracking-wider text-sm text-foreground hover:text-primary transition-colors py-2 px-1 after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-[1px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  closeMenu
}: {
  closeMenu: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <div className="flex flex-col py-4">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} onClick={closeMenu} className="block px-4 py-3 font-default uppercase tracking-wider text-sm text-foreground hover:text-primary hover:bg-accent/5 transition-colors border-l-2 border-transparent hover:border-primary">
            {item.label}
          </Link>
        </div>)}
      <div className="px-4 pt-4 mt-2 border-t border-border/20">
        <Button asChild className="w-full font-default uppercase tracking-wider text-xs bg-primary text-primary-foreground shadow-[0_0_15px_var(--color-primary)] rounded-sm">
          <Link to="#" onClick={closeMenu}>Get a Quote</Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className={`dark bg-background text-foreground sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/20" : "bg-background"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634081/img/foodagritech_logo_minimal.png" alt="Boat Bathers" className="h-8 w-auto" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center justify-end gap-4">
            <div className="hidden md:block">
              <Button asChild className="relative font-default uppercase tracking-wider text-xs bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_var(--color-primary)] hover:shadow-[0_0_25px_var(--color-primary)] transition-all duration-300 border border-primary/20 rounded-sm">
                <Link to="#">Get a Quote</Link>
              </Button>
            </div>
            
            {}
            <div className="md:hidden flex items-center">
              <Button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-accent/5" aria-expanded={isMobileMenuOpen}>
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? "max-h-[500px] opacity-100 border-b border-border/20 bg-background/95 backdrop-blur-md" : "max-h-0 opacity-0"}`}>
        <MenuProvider menu_id="16">
          <MobileMenuConsumer closeMenu={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </section>;
}